import {
    createCommerce,
    createPathStorefrontAliasResolver,
    type Commerce,
    type CommerceCart,
    type CommerceDataSource,
    type PathStorefrontAliasResolverOptions,
    type CommerceProductDetailConfiguration,
    type CommerceStorefrontConfiguration,
} from "@teqtank/commerce-sdk";
import { applyCommerceTheme, type CommerceTheme } from "@teqtank/commerce-theme-default";
import { HttpCommerceTransport } from "@teqtank/commerce-transport";

const COMPONENT_NAMES = [
    "teqtank-products",
    "teqtank-product-detail",
    "teqtank-cart-toolbox",
    "teqtank-cart",
    "teqtank-login",
    "teqtank-customer-menu",
    "teqtank-market-selector",
    "teqtank-checkout",
    "teqtank-enrollment",
    "teqtank-storefront-context",
    "teqtank-storefront-banner",
    "teqtank-storefront-notice",
    "teqtank-contact-card",
    "teqtank-affiliate-tools",
    "teqtank-share-panel",
    "teqtank-share-button",
    "teqtank-share-receiver",
] as const;
const COMPONENT_SELECTOR = COMPONENT_NAMES.join(",");
const COMPONENT_PLACEHOLDER_SELECTOR = "[data-teqtank-component]";
const DIRECT_ADD_SELECTOR = "[data-teqtank-add-product]";
export const OPEN_CART_INTENT_KEY = "teqtank:open-cart-on-arrival";
const COMPONENT_NAME_SET = new Set<string>(COMPONENT_NAMES);

interface CommerceElement extends HTMLElement {
    commerce?: Commerce;
}

interface CartToolboxElement extends CommerceElement {
    openOnAdd: boolean;
    readonly updateComplete?: Promise<unknown>;
    open(): void;
}

export interface TeqtankCommerceEmbedOptions {
    readonly gatewayUrl: string;
    readonly commerceKey: string;
    readonly dataSource?: CommerceDataSource;
    readonly productDetail?: CommerceProductDetailConfiguration;
    readonly storefront?: CommerceStorefrontConfiguration;
    readonly theme?: CommerceTheme;
    readonly root?: ParentNode;
    readonly observeComponents?: boolean;
}

export interface TeqtankCommerceEmbed {
    readonly commerce: Commerce;
    addProduct(request: TeqtankCommerceDirectAddRequest): Promise<CommerceCart>;
    bind(root?: ParentNode): number;
    mount(root?: ParentNode): number;
    dispose(): void;
}

export interface TeqtankCommerceDirectAddRequest {
    readonly query: string;
    readonly purchaseType?: "one-time" | "recurring";
    readonly quantity?: number;
    readonly experience?: string;
    readonly replaceCart?: boolean;
    readonly successRoute?: string;
    readonly openCart?: boolean;
}

export interface TeqtankCommerceEmbedApi {
    initialize(options: TeqtankCommerceEmbedOptions): Promise<TeqtankCommerceEmbed>;
    createPathStorefrontAliasResolver(
        options?: PathStorefrontAliasResolverOptions,
    ): CommerceStorefrontConfiguration["resolveAlias"];
}

export async function initializeCommerceEmbed(
    options: TeqtankCommerceEmbedOptions,
): Promise<TeqtankCommerceEmbed> {
    const gatewayUrl = requireAbsoluteUrl(options.gatewayUrl, "gatewayUrl");
    const commerceKey = requireValue(options.commerceKey, "commerceKey");
    const root = options.root ?? document;
    const transport = new HttpCommerceTransport({ baseUrl: gatewayUrl });

    try {
        const commerce = await createCommerce({
            commerceKey,
            dataSource: options.dataSource ?? "connected",
            transport,
            ...(options.productDetail === undefined
                ? {}
                : { productDetail: options.productDetail }),
            ...(options.storefront === undefined ? {} : { storefront: options.storefront }),
        });
        await commerce.customer.refreshSession();

        // Prime existing declarative elements while they are still inert, then register and
        // upgrade them with the connected runtime already assigned. This prevents an autonomous
        // fallback runtime from starting during a slow managed bootstrap. The production build
        // remains a single file because browser-embed disables code splitting.
        bindCommerceComponents(root, commerce);
        await import("@teqtank/commerce-components");

        if (options.theme !== undefined)
            applyCommerceTheme(options.theme, document.documentElement);

        const directActions = createDirectActionBinder(commerce);
        const bind = (target: ParentNode = root): number =>
            bindCommerceComponents(target, commerce) + directActions.bind(target);
        const mount = (target: ParentNode = root): number =>
            mountCommerceComponents(target, commerce);
        mount();
        bind();
        void consumeOpenCartIntent(root);

        const observer =
            options.observeComponents === false
                ? undefined
                : createComponentObserver(root, commerce, directActions.bind);
        const result: TeqtankCommerceEmbed = Object.freeze({
            commerce,
            addProduct: async (request: TeqtankCommerceDirectAddRequest) => {
                const cart = await addProduct(commerce, request);
                completeDirectAddNavigation(request);
                return cart;
            },
            bind,
            mount,
            dispose: () => {
                observer?.disconnect();
                directActions.dispose();
                commerce.dispose();
            },
        });

        document.dispatchEvent(
            new CustomEvent("teqtank-commerce-ready", {
                detail: Object.freeze({ commerce, embed: result }),
            }),
        );
        return result;
    } catch (error) {
        document.dispatchEvent(
            new CustomEvent("teqtank-commerce-error", {
                detail: Object.freeze({ error }),
            }),
        );
        throw error;
    }
}

export function bindCommerceComponents(root: ParentNode, commerce: Commerce): number {
    const elements = new Set<CommerceElement>();
    if (root instanceof Element && root.matches(COMPONENT_SELECTOR))
        elements.add(root as CommerceElement);
    root.querySelectorAll<CommerceElement>(COMPONENT_SELECTOR).forEach((element) =>
        elements.add(element),
    );
    elements.forEach((element) => {
        element.commerce = commerce;
    });
    return elements.size;
}

export function mountCommerceComponents(root: ParentNode, commerce: Commerce): number {
    const placeholders = new Set<HTMLElement>();
    if (root instanceof HTMLElement && root.matches(COMPONENT_PLACEHOLDER_SELECTOR))
        placeholders.add(root);
    root.querySelectorAll<HTMLElement>(COMPONENT_PLACEHOLDER_SELECTOR).forEach((element) =>
        placeholders.add(element),
    );

    let mounted = 0;
    for (const placeholder of placeholders) {
        const name = placeholder.dataset.teqtankComponent?.trim().toLowerCase();
        if (name === undefined || !COMPONENT_NAME_SET.has(name)) {
            console.warn("Ignoring unsupported Teqtank Commerce component placeholder.", { name });
            continue;
        }
        const component = document.createElement(name) as CommerceElement;
        for (const attribute of placeholder.attributes) {
            if (attribute.name !== "data-teqtank-component")
                component.setAttribute(attribute.name, attribute.value);
        }
        component.commerce = commerce;
        placeholder.replaceWith(component);
        mounted += 1;
    }
    return mounted;
}

function createComponentObserver(
    root: ParentNode,
    commerce: Commerce,
    bindDirectActions: (root: ParentNode) => number,
): MutationObserver | undefined {
    if (typeof MutationObserver !== "function") return undefined;
    const observer = new MutationObserver((records) => {
        for (const record of records) {
            for (const node of record.addedNodes) {
                if (node instanceof Element) mountCommerceComponents(node, commerce);
                if (node instanceof Element) bindCommerceComponents(node, commerce);
                if (node instanceof Element) bindDirectActions(node);
            }
        }
        void consumeOpenCartIntent(root);
    });
    observer.observe(root, { childList: true, subtree: true });
    return observer;
}

function createDirectActionBinder(commerce: Commerce): {
    bind(root: ParentNode): number;
    dispose(): void;
} {
    const listeners = new Map<HTMLElement, EventListener>();
    const bind = (root: ParentNode): number => {
        const elements = new Set<HTMLElement>();
        if (root instanceof HTMLElement && root.matches(DIRECT_ADD_SELECTOR)) elements.add(root);
        root.querySelectorAll<HTMLElement>(DIRECT_ADD_SELECTOR).forEach((element) =>
            elements.add(element),
        );

        let mounted = 0;
        for (const element of elements) {
            if (listeners.has(element)) continue;
            const listener: EventListener = (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (element.getAttribute("aria-busy") === "true") return;
                const query = element.dataset.teqtankAddProduct?.trim() ?? "";
                const purchaseType =
                    element.dataset.teqtankPurchaseType === "recurring" ? "recurring" : "one-time";
                const quantity = Number.parseInt(element.dataset.teqtankQuantity ?? "1", 10);
                const experience = element.dataset.teqtankExperience?.trim();
                const successRoute = element.dataset.teqtankSuccessRoute?.trim();
                const restoreCartToolboxes = successRoute
                    ? suspendCartToolboxAutoOpen(element.ownerDocument)
                    : () => undefined;
                element.setAttribute("aria-busy", "true");
                element.dataset.teqtankActionState = "adding";
                void addProduct(commerce, {
                    query,
                    purchaseType,
                    quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1,
                    ...(experience ? { experience } : {}),
                    replaceCart: parseBooleanDataAttribute(element.dataset.teqtankReplaceCart),
                    ...(successRoute ? { successRoute } : {}),
                    openCart: parseBooleanDataAttribute(element.dataset.teqtankOpenCart),
                })
                    .then((cart) => {
                        element.dataset.teqtankActionState = "added";
                        element.dispatchEvent(
                            new CustomEvent("teqtank-direct-product-added", {
                                detail: Object.freeze({ query, cart }),
                                bubbles: true,
                                composed: true,
                            }),
                        );
                        completeDirectAddNavigation(requestFromElement(element));
                    })
                    .catch((error) => {
                        element.dataset.teqtankActionState = "error";
                        element.dispatchEvent(
                            new CustomEvent("teqtank-direct-product-add-failed", {
                                detail: Object.freeze({ query, error }),
                                bubbles: true,
                                composed: true,
                            }),
                        );
                        console.error("Teqtank Commerce direct add failed.", error);
                    })
                    .finally(() => {
                        restoreCartToolboxes();
                        element.removeAttribute("aria-busy");
                    });
            };
            element.addEventListener("click", listener);
            listeners.set(element, listener);
            mounted += 1;
        }
        return mounted;
    };
    return {
        bind,
        dispose: () => {
            for (const [element, listener] of listeners)
                element.removeEventListener("click", listener);
            listeners.clear();
        },
    };
}

async function addProduct(
    commerce: Commerce,
    request: TeqtankCommerceDirectAddRequest,
): Promise<CommerceCart> {
    validateDirectAddSuccessRoute(request.successRoute);
    const query = requireValue(request.query, "query");
    const response = await commerce.products.search({
        ...(request.experience ? { experience: request.experience } : {}),
        query,
        sort: "default",
        page: 1,
        pageSize: 100,
    });
    const normalized = query.toLocaleLowerCase();
    const summary =
        (response.items.length === 1 ? response.items[0] : undefined) ??
        response.items.find((item) => item.id.toLocaleLowerCase() === normalized) ??
        response.items.find((item) => item.slug.toLocaleLowerCase() === normalized) ??
        response.items.find((item) => item.displaySku?.toLocaleLowerCase() === normalized) ??
        response.items.find((item) => item.name.toLocaleLowerCase() === normalized) ??
        undefined;
    if (summary === undefined)
        throw new Error(`No available product matched '${query}' in the selected market.`);

    const purchaseType = request.purchaseType ?? "one-time";

    return commerce.cart.add({
        productId: summary.id,
        quantity: request.quantity ?? 1,
        purchaseType,
        ...(request.experience ? { experience: request.experience } : {}),
        ...(request.replaceCart === true ? { replaceCart: true } : {}),
    });
}

function requestFromElement(element: HTMLElement): TeqtankCommerceDirectAddRequest {
    const successRoute = element.dataset.teqtankSuccessRoute?.trim();
    return {
        query: element.dataset.teqtankAddProduct?.trim() ?? "",
        ...(successRoute ? { successRoute } : {}),
        openCart: parseBooleanDataAttribute(element.dataset.teqtankOpenCart),
    };
}

function completeDirectAddNavigation(request: TeqtankCommerceDirectAddRequest): void {
    if (!request.successRoute) return;
    const target = validateDirectAddSuccessRoute(request.successRoute);
    if (request.openCart === true)
        window.sessionStorage.setItem(OPEN_CART_INTENT_KEY, target.pathname);
    window.location.assign(target.href);
}

function validateDirectAddSuccessRoute(successRoute: string | undefined): URL {
    const target = new URL(successRoute ?? window.location.href, window.location.href);
    if (target.origin !== window.location.origin)
        throw new TypeError("Direct-add successRoute must remain on the current site origin.");
    return target;
}

export async function consumeOpenCartIntent(root: ParentNode): Promise<boolean> {
    const targetPath = window.sessionStorage.getItem(OPEN_CART_INTENT_KEY);
    if (targetPath === null || targetPath !== window.location.pathname) return false;
    const toolbox = root.querySelector<CartToolboxElement>("teqtank-cart-toolbox");
    if (toolbox === null) return false;

    await toolbox.updateComplete;
    if (!toolbox.isConnected) return false;

    toolbox.open();
    window.sessionStorage.removeItem(OPEN_CART_INTENT_KEY);
    return true;
}

export function suspendCartToolboxAutoOpen(root: ParentNode): () => void {
    const toolboxes = Array.from(
        root.querySelectorAll<CartToolboxElement>("teqtank-cart-toolbox"),
    ).map((toolbox) => ({ toolbox, openOnAdd: toolbox.openOnAdd }));
    for (const { toolbox } of toolboxes) toolbox.openOnAdd = false;

    return () => {
        for (const { toolbox, openOnAdd } of toolboxes) toolbox.openOnAdd = openOnAdd;
    };
}

function parseBooleanDataAttribute(value: string | undefined): boolean {
    return value === "" || value?.toLowerCase() === "true";
}

function requireAbsoluteUrl(value: string, property: string): string {
    const normalized = requireValue(value, property);
    const url = new URL(normalized);
    if (url.protocol !== "https:" && url.protocol !== "http:")
        throw new TypeError(`${property} must use HTTP or HTTPS.`);
    return url.toString().replace(/\/$/u, "");
}

function requireValue(value: string, property: string): string {
    if (typeof value !== "string" || value.trim().length === 0)
        throw new TypeError(`${property} is required.`);
    return value.trim();
}

const api: TeqtankCommerceEmbedApi = Object.freeze({
    initialize: initializeCommerceEmbed,
    createPathStorefrontAliasResolver,
});

globalThis.TeqtankCommerceEmbed = api;

declare global {
    var TeqtankCommerceEmbed: TeqtankCommerceEmbedApi;
}
